<template>
  <div class="page">
    <menu-pages-navbar class="mb-3" />
    <!-- Header -->
    <div class="subheading mt-5">
      {{ $t('menuStartHeader') }}
    </div>
    <!-- Tab Buttons -->
    <v-layout
      align-center
      class="mt-4"
    >
      <v-btn
        flat
        round
        :color="tab === 'pricing' ? 'grey darken-4' : 'grey darken-1'"
        class="subheading"
        style="text-transform: none; margin-left: -16px"
        @click="tab = 'pricing'"
      >
        {{ $t('menuStartPricingButton') }}
      </v-btn>
      <v-btn
        flat
        round
        :color="tab === 'steps' ? 'grey darken-4' : 'grey darken-1'"
        class="subheading"
        style="text-transform: none"
        @click="
          currentStepIndex = 1;
          tab = 'steps';
        "
      >
        {{ $t('menuStartHowToButton') }}
      </v-btn>
      <v-btn
        flat
        round
        color="grey darken-1"
        class="subheading"
        style="text-transform: none"
        :href="bookADateWithCalendlyUrl"
        target="_blank"
      >
        {{ $t('menuStartBookADateButton') }}
      </v-btn>
    </v-layout>
    <div>
      <!-- Tab 1: Pricing -->
      <div
        v-if="tab == 'pricing'"
        class="mt-4"
      >
        <v-data-table
          hide-actions
          disable-initial-sort
          :headers="planFeatureHeaders"
          :items="planFeatureRows"
          class="plan-feature-rows-table"
        >
          <!-- Headers -->
          <template
            slot="headers"
            slot-scope="props"
          >
            <tr>
              <th
                v-for="(header, headerIndex) in props.headers"
                :key="headerIndex"
                class="column text-xs-left"
                :width="header.width || 'initial'"
              >
                <v-layout
                  v-if="header.isPlan"
                  justify-center
                  class="my-3"
                  style="line-height: 1.33"
                >
                  <div>
                    <div class="caption grey--text text--darken-1 mb-1">
                      {{ $t('menuStartPricingPlan') }}
                    </div>
                    <div class="title black--text">
                      {{ header.text }}
                    </div>
                  </div>
                </v-layout>
                <div
                  v-else
                  class="title black--text"
                  style="margin-top: 20px"
                >
                  {{ header.text }}
                </div>
              </th>
            </tr>
          </template>
          <!-- Rows -->
          <template
            slot="items"
            slot-scope="props"
          >
            <tr
              :key="props.item.id"
              class="unselectable"
            >
              <!-- Feature Name -->
              <td
                :class="{
                  'py-2': props.item.details && props.item.details.length > 0,
                  clickable:
                    props.item.details && props.item.details.length > 0,
                }"
                :title="
                  props.item.details && props.item.details.length > 0
                    ? props.item.expanded
                      ? $t('clickToCollapse')
                      : $t('clickToExpand')
                    : ''
                "
                @click.stop="
                  props.item.details && props.item.details.length > 0
                    ? (props.item.expanded = !props.item.expanded)
                    : null
                "
              >
                <v-layout align-center>
                  {{ props.item.feature }}
                  <v-icon
                    v-if="
                      props.item.details &&
                        props.item.details.length > 0 &&
                        props.item.expanded
                    "
                    class="ml-1 info--text"
                    medium
                  >
                    keyboard_arrow_up
                  </v-icon>
                  <v-icon
                    v-if="
                      props.item.details &&
                        props.item.details.length > 0 &&
                        !props.item.expanded
                    "
                    class="ml-1 info--text"
                    medium
                  >
                    keyboard_arrow_down
                  </v-icon>
                </v-layout>
                <v-slide-y-transition>
                  <ul
                    v-if="
                      props.item.details &&
                        props.item.details.length > 0 &&
                        props.item.expanded
                    "
                    class="mt-1 caption grey--text text--darken-2"
                  >
                    <li
                      v-for="(detail, detailIndex) in props.item.details"
                      :key="detailIndex"
                    >
                      {{ $t(detail) }}
                    </li>
                  </ul>
                </v-slide-y-transition>
              </td>
              <!-- Plans and whether they have the feature -->
              <td
                v-for="(flag, flagIndex) in props.item.flags"
                :key="flagIndex"
                class="text-xs-center"
              >
                <v-icon
                  v-if="flag"
                  color="success"
                  :title="
                    props.item.trueTitle ||
                      $t('menuStartPricingFeatureIncluded')
                  "
                  v-html="props.item.trueIcon || 'check'"
                />
                <v-icon
                  v-else
                  color="error"
                  :title="$t('menuStartPricingFeatureExcluded')"
                >
                  close
                </v-icon>
              </td>
              <!-- Prices (last row only)-->
              <td
                v-for="(price, priceIndex) in props.item.prices"
                :key="priceIndex"
                class="pt-2 pb-1"
                style="line-height: 1.33"
              >
                <v-layout justify-center>
                  <div>
                    <div
                      style="font-size: 2.33em"
                      class="nowrap"
                    >
                      {{ price }} €
                    </div>
                    <div class="caption grey--text text--darken-1 mb-2">
                      {{ $t('menuStartPricingPerMonth') }}*
                    </div>
                  </div>
                </v-layout>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div class="mt-4 subheading text-xs-center">
          <b>{{ $t('menuStartPricingPlusSetup_1') }}</b><br>
          {{ $t('menuStartPricingPlusSetup_2') }}
          <div
            class="grey--text text--darken-1 mt-3"
            style="font-size: 12px"
          >
            * {{ $t('menuStartPricingPerMonthDetails') }}
          </div>
        </div>
      </div>
      <!-- Tab 2: Steps -->
      <div
        v-if="tab == 'steps'"
        class="mt-4"
      >
        <menu-how-to-stepper
          :step-index.sync="currentStepIndex"
          class="mt-3"
        />
      </div>
      <!-- 'Interested'-Button -->
      <v-layout justify-center>
        <v-slide-y-transition>
          <v-btn
            v-if="!!menu && !menu.has_paid_feature_interest_submitted"
            round
            color="msr"
            dark
            :fixed="$vuetify.breakpoint.height < 1200"
            :bottom="$vuetify.breakpoint.height < 1200"
            :large="$vuetify.breakpoint.smAndUp"
            class="mx-0 mt-4"
            :loading="submittingInterest"
            @click="submitInterestAndOpenDialog()"
          >
            {{ $t('menuStartSubmitInterestedButton') }}
          </v-btn>
        </v-slide-y-transition>
      </v-layout>
    </div>
    <!-- Scroll Spacer -->
    <div style="min-height: 60px" />
    <!-- 'Interest submitted' Dialog -->
    <generic-confirmation-dialog
      :show.sync="showInterestSubmittedDialog"
      :loading="submittingInterest"
      persistent
      close-on-confirm
    >
      <h1
        slot="title"
        class="headline"
      >
        <v-layout
          row
          align-center
          justify-center
          wrap
        >
          <span class="text-xs-center">
            {{ $t('menuStartInterestSubmittedDialogTitle') }}
          </span>
        </v-layout>
      </h1>
      <div
        slot="content"
        class="subheading grey--text text--darken-3"
      >
        {{ $t('menuStartInterestSubmittedDialogBody') }}
      </div>
    </generic-confirmation-dialog>
  </div>
</template>

<script>
import GenericConfirmationDialog from '@/components/GenericConfirmationDialog';
import MenuHowToStepper from '@/components/Menu/MenuHowToStepper';
import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';

export default {
  components: {
    GenericConfirmationDialog,
    MenuHowToStepper,
    MenuPagesNavbar,
  },
  data() {
    return {
      tab: 'pricing', // One of: 'steps', 'pricing'
      currentStepIndex: 1, // Note: Must start at 1, v-stepper can't handle a 0.

      submittingInterest: false,
      showInterestSubmittedDialog: false,

      planFeatureHeaders: [
        {
          isPlan: false,
          text: this.$t('menuStartPricingFeature'),
          value: 'feature',
          sortable: false,
          width: '75%',
        },
        {
          isPlan: true,
          text: this.$t('menuStartPricingStarterName'),
          value: 'starter',
          sortable: false,
        },
        {
          isPlan: true,
          text: this.$t('menuStartPricingProName'),
          value: 'pro',
          sortable: false,
        },
      ],

      // Note: When there are details, make sure to also add an 'expanded' flag.
      planFeatureRows: [
        {
          feature: this.$t('menuStartPricingStarterFeature_03'),
          details: [
            'menuStartPricingStarterFeature_03_detail_01',
            'menuStartPricingStarterFeature_03_detail_02',
            'menuStartPricingStarterFeature_03_detail_03',
          ],
          expanded: false,
          flags: [true, true],
        },
        {
          feature: this.$t('menuStartPricingStarterFeature_04'),
          details: [
            'menuStartPricingStarterFeature_04_detail_01',
            'menuStartPricingStarterFeature_04_detail_02',
            'menuStartPricingStarterFeature_04_detail_03',
            'menuStartPricingStarterFeature_04_detail_04',
            'menuStartPricingStarterFeature_04_detail_05',
          ],
          expanded: false,
          flags: [true, true],
        },
        {
          feature: this.$t('menuStartPricingStarterFeature_04b'),
          flags: [true, true],
        },
        {
          feature: this.$t('menuStartPricingStarterFeature_05'),
          flags: [true, true],
        },
        {
          feature: this.$t('menuStartPricingStarterFeature_06'),
          flags: [true, true],
        },
        {
          feature: this.$t('menuStartPricingStarterFeature_07'),
          details: [
            'menuStartPricingStarterFeature_07_detail_01',
            'menuStartPricingStarterFeature_07_detail_02',
            'menuStartPricingStarterFeature_07_detail_03',
          ],
          expanded: false,
          flags: [true, true],
        },
        {
          feature: this.$t('menuStartPricingProFeature_01'),
          flags: [false, true],
        },
        {
          feature: this.$t('menuStartPricingProFeature_02'),
          flags: [false, true],
        },
        {
          feature: this.$t('menuStartPricingProFeature_03'),
          flags: [false, true],
          trueIcon: 'euro',
          trueTitle: this.$t('menuStartPricingFeatureHasAdditionalCost'),
        },
        {
          feature: '',
          flags: [],
          prices: [25, 45],
        },
      ],
    };
  },
  computed: {
    bookADateWithCalendlyUrl() {
      return 'https://calendly.com/myshopradio-tv/digitale-speisekarte';
    },
    menuId() {
      return this.$route.params.menuId;
    },
    menu() {
      return this.$store.state.common.menus.filter(
        (menu) => menu.id == this.menuId
      )[0];
    },
  },
  created() {
    // Demo setup may have finished creation just before, be sure to
    // fetch that demo Menu so we e.g. may submit interest.
    this.$store.dispatch('LIST_MENUS');
  },
  methods: {
    submitInterestAndOpenDialog() {
      this.submitInterestForPaidMenusFeature().then(() => {
        this.showInterestSubmittedDialog = true;

        // Frontend update
        if (this.menu) {
          this.menu.has_paid_feature_interest_submitted = true;
        }

        // Reload menus to update the 'interested' flag.
        this.$store.dispatch('LIST_MENUS');
      });
    },
    submitInterestForPaidMenusFeature() {
      this.submittingInterest = true;
      return this.$api
        .submitInterestForPaidMenusFeature(this.menuId)
        .finally(() => {
          this.submittingInterest = false;
        });
    },
  },
};
</script>

<style scoped>
.pricing--features li {
  margin-bottom: 6px;
}

@media (max-width: 600px) {
  .plan-feature-rows-table >>> th,
  td {
    /* Less padding on mobile. */
    padding: 0 6px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/MenuStart.vue